import React, { useState } from "react";
import { Plus, Sparkles } from "lucide-react";
import { useParams } from "react-router-dom";

import NoDataIcon from "../../../assets/svg/EmptyTableIcon";
import ButtonV2 from "../../../components/ui/button/v2";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CommonJobsStatus } from "../../../api/fraudElements";
import { selectGeneratedFraudElementsForParty } from "../logic/fraudElementsSlice";
import { generateFraudElements } from "../logic/fraudElementsThunk";
import { showErrorToast } from "../../../lib/toast";
import CreateFraudElementsModal from "./createFraudElementModal";

interface GenerateFraudElementsEmptyStateProps {
  partyId: string;
}

const GenerateFraudElementsEmptyState: React.FC<
  GenerateFraudElementsEmptyStateProps
> = ({ partyId }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const generatedFraudElements = useAppSelector((state) =>
    selectGeneratedFraudElementsForParty(state, partyId),
  );

  const hasGenerationFailed = generatedFraudElements?.status === CommonJobsStatus.FAILED;

  const hasNoConclusiveFraudElements =
    generatedFraudElements?.status === CommonJobsStatus.AVAILABLE &&
    generatedFraudElements.fraud_elements.length === 0;

  const hasNoGenerationResult = generatedFraudElements === null;

  const [isGenerating, setIsGenerating] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleGenerate = async () => {
    if (!caseId || isGenerating) {
      return;
    }

    setIsGenerating(true);

    try {
      await dispatch(
        generateFraudElements({ case_id: caseId, party_id: partyId }),
      ).unwrap();
    } catch {
      showErrorToast("Failed to generate fraud elements. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <div className="flex w-[480px] flex-col items-center gap-6 p-6">
        <NoDataIcon />

        <div className="flex w-[320px] flex-col items-center gap-2">
          <h3 className="text-center text-base font-semibold leading-6 text-[#0A0A0A]">
            {hasNoConclusiveFraudElements &&
              "No Fraud elements could be conclusively found based on current data"}
            {hasGenerationFailed && "Fraud Elements failed to generate"}
            {hasNoGenerationResult && "No fraud elements created yet"}
          </h3>

          {!hasNoConclusiveFraudElements && (
            <p className="text-center text-sm leading-5 text-[#737373]">
              {hasGenerationFailed
                ? "Create the fraud elements for this party manually to capture fraud details"
                : "Create the first fraud element for this party to capture fraud details"}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {!hasNoConclusiveFraudElements && (
            <ButtonV2
              variant="ai-gradient"
              size="md"
              loading={isGenerating}
              onClick={handleGenerate}
              prefix={<Sparkles className="h-4 w-4 text-[#FAFAFA]" />}
              className="h-9 rounded-[8px] bg-[linear-gradient(106deg,#3551F3_0%,#4B6EE3_45.06%,#00B5E2_100%)] text-[#FAFAFA]"
            >
              Generate Fraud Elements
            </ButtonV2>
          )}

          <ButtonV2
            variant="outline"
            size="md"
            onClick={() => setIsCreateModalOpen(true)}
            prefix={<Plus className="h-4 w-4 text-[#0A0A0A]" />}
            className="h-9 rounded-[8px] border-[#E5E5E5] text-[#0A0A0A]"
          >
            Create Manually
          </ButtonV2>
        </div>
      </div>

      {isCreateModalOpen && (
        <CreateFraudElementsModal onClose={() => setIsCreateModalOpen(false)} />
      )}
    </>
  );
};

export default GenerateFraudElementsEmptyState;